/**
 * Admin impersonation client helpers.
 *
 * Storage scheme while impersonating:
 *   hm_token        — short-lived impersonation JWT (target user's id, imp claim)
 *   hm_user         — target user's profile (what AuthContext sees)
 *   hm_token_admin  — the admin's original JWT, restored on return
 *   hm_user_admin   — the admin's original profile, restored on return
 *   hm_imp_meta     — JSON metadata used by the top-bar banner:
 *                     { admin_id, admin_name, target_id, target_name, target_role,
 *                       reason, ticket_id, expires_at }
 *
 * When NOT impersonating, only the first two keys are present.
 */
import API from '../api/axios';

const K = {
  TOKEN:        'hm_token',
  USER:         'hm_user',
  ADMIN_TOKEN:  'hm_token_admin',
  ADMIN_USER:   'hm_user_admin',
  META:         'hm_imp_meta',
};

export function isImpersonating() {
  return !!localStorage.getItem(K.ADMIN_TOKEN) && !!localStorage.getItem(K.META);
}

export function getImpersonationMeta() {
  try {
    const raw = localStorage.getItem(K.META);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Start an impersonation session.
 * Throws on API error so caller can toast the message.
 */
export async function startImpersonation({ targetUserId, reason, ticketId, setUser, navigate }) {
  if (isImpersonating()) throw new Error('Already impersonating');
  const body = { reason };
  if (ticketId) body.ticket_id = ticketId;

  const { data } = await API.post(`/support/admin/users/${targetUserId}/impersonate`, body);
  // Cache admin tokens before swap so we can restore on return.
  const adminToken = localStorage.getItem(K.TOKEN);
  const adminUser  = localStorage.getItem(K.USER);
  if (adminToken) localStorage.setItem(K.ADMIN_TOKEN, adminToken);
  if (adminUser)  localStorage.setItem(K.ADMIN_USER,  adminUser);

  // Swap to the impersonation token.
  localStorage.setItem(K.TOKEN, data.token);
  localStorage.setItem(K.USER,  JSON.stringify(data.user));
  localStorage.setItem(K.META, JSON.stringify({
    admin_id:    data.impersonation.admin_id,
    admin_name:  data.impersonation.admin_name,
    target_id:   data.user.id,
    target_name: data.user.name,
    target_role: data.user.role,
    reason:      data.impersonation.reason,
    ticket_id:   data.impersonation.ticket_id,
    expires_at:  data.expires_at,
  }));

  setUser?.(data.user);
  // Send the admin to the impersonated user's home so they can do whatever
  // they need (raise an enquiry on their behalf, view a payment, etc.).
  navigate?.(`/${data.user.role}`, { replace: true });
  return data;
}

/**
 * End the current impersonation session.
 * If `skipApi` is true (e.g. when the imp token already expired) we don't
 * bother calling the backend.
 */
export async function endImpersonation({ setUser, navigate, skipApi = false } = {}) {
  const meta = getImpersonationMeta();
  if (!skipApi) {
    try { await API.post('/auth/impersonate/end'); } catch { /* token may be dead; restore anyway */ }
  }

  const adminToken = localStorage.getItem(K.ADMIN_TOKEN);
  const adminUserRaw = localStorage.getItem(K.ADMIN_USER);

  // Always purge impersonation keys first
  localStorage.removeItem(K.META);
  localStorage.removeItem(K.ADMIN_TOKEN);
  localStorage.removeItem(K.ADMIN_USER);

  if (adminToken) {
    localStorage.setItem(K.TOKEN, adminToken);
    if (adminUserRaw) {
      try {
        const adminUser = JSON.parse(adminUserRaw);
        localStorage.setItem(K.USER, adminUserRaw);
        setUser?.(adminUser);
      } catch { /* fall through */ }
    }
    const back = meta?.ticket_id
      ? `/admin/support?ticket=${meta.ticket_id}`
      : '/admin/support';
    navigate?.(back, { replace: true });
  } else {
    // No admin token cached — corrupted state, send to login
    localStorage.removeItem(K.TOKEN);
    localStorage.removeItem(K.USER);
    setUser?.(null);
    navigate?.('/login', { replace: true });
  }
}
